import { useState } from "react";
import { X, Link2, Check, Twitter, Facebook } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

// kind: "gladiator" | "battle"
export default function ShareModal({ open, onClose, kind, id, text }) {
  const { user } = useAuth();
  const [copied, setCopied] = useState(false);
  if (!open) return null;

  const seg = kind === "gladiator" ? "g" : "b";
  const referralParam = user?.referral_code ? `?ref=${user.referral_code}` : "";
  const shareUrl = `${BACKEND}/api/s/${seg}/${id}${referralParam}`;
  const imageUrl = `${BACKEND}/api/share/${seg}/${id}/image.png`;
  const shareText = text || (kind === "gladiator" ? "Check out my gladiator on Lanista!" : "Watch this arena bout on Lanista!");

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      toast.success("Link copied — paste it anywhere (Discord, chat, etc.)");
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Couldn't copy — long-press the link to copy manually.");
    }
  };

  const open_ = (url) => window.open(url, "_blank", "noopener,noreferrer,width=600,height=540");
  const X_URL = `https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(shareUrl)}`;
  const REDDIT_URL = `https://www.reddit.com/submit?url=${encodeURIComponent(shareUrl)}&title=${encodeURIComponent(shareText)}`;
  const FB_URL = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`;

  const SocialBtn = ({ onClick, label, children, testid }) => (
    <button data-testid={testid} onClick={onClick}
      className="flex flex-col items-center gap-2 px-3 py-3 border border-zinc-800 bg-zinc-900/40 hover:border-amber-700/60 hover:bg-amber-950/20 transition-colors rounded-sm">
      {children}
      <span className="text-[11px] uppercase tracking-wider text-zinc-400">{label}</span>
    </button>
  );

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4" data-testid="share-modal">
      <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-zinc-950 border border-amber-700/40 shadow-[0_0_40px_rgba(0,0,0,0.8)] w-full max-w-md max-h-[88vh] overflow-y-auto fade-up">
        <div className="flex items-center justify-between px-5 py-4 border-b border-amber-900/30">
          <h3 className="font-cinzel text-lg text-amber-300 uppercase tracking-wide">Share</h3>
          <button data-testid="share-modal-close" onClick={onClose} className="text-zinc-500 hover:text-amber-400"><X size={20} /></button>
        </div>
        <div className="p-5">
          <div className="border border-amber-900/30 bg-black/40 mb-4 overflow-hidden">
            <img src={imageUrl} alt="Share card preview" data-testid="share-card-preview"
              className="w-full block" loading="lazy" />
          </div>
          <p className="text-xs text-zinc-500 mb-4">Links unfurl with this card on X, Discord, Reddit & more — and bring visitors straight to the arena.</p>

          <div className="grid grid-cols-4 gap-2 mb-4">
            <SocialBtn testid="share-x" label="X" onClick={() => open_(X_URL)}>
              <Twitter size={20} className="text-sky-400" />
            </SocialBtn>
            <SocialBtn testid="share-reddit" label="Reddit" onClick={() => open_(REDDIT_URL)}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" className="text-orange-500"><path d="M12 0a12 12 0 100 24 12 12 0 000-24zm6.18 13.06a1.6 1.6 0 01-.9 1.46c.01.13.02.26.02.39 0 2.4-2.8 4.35-6.25 4.35s-6.25-1.95-6.25-4.35c0-.13.01-.26.02-.39a1.6 1.6 0 111.76-2.62 7.85 7.85 0 014.24-1.34l.8-3.76a.34.34 0 01.4-.26l2.65.56a1.13 1.13 0 11-.14.66l-2.37-.5-.71 3.36a7.83 7.83 0 014.18 1.34 1.6 1.6 0 012.55 1.43zM8.7 13.4a1.13 1.13 0 102.26 0 1.13 1.13 0 00-2.26 0zm5.49 2.96a.34.34 0 00-.48 0 3.07 3.07 0 01-2.21.74 3.07 3.07 0 01-2.2-.74.34.34 0 00-.48.48 3.74 3.74 0 002.68.93 3.74 3.74 0 002.69-.93.34.34 0 000-.48zm-.16-1.83a1.13 1.13 0 102.26 0 1.13 1.13 0 00-2.26 0z"/></svg>
            </SocialBtn>
            <SocialBtn testid="share-facebook" label="Facebook" onClick={() => open_(FB_URL)}>
              <Facebook size={20} className="text-blue-500" />
            </SocialBtn>
            <SocialBtn testid="share-copy" label={copied ? "Copied" : "Copy"} onClick={copy}>
              {copied ? <Check size={20} className="text-emerald-400" /> : <Link2 size={20} className="text-amber-400" />}
            </SocialBtn>
          </div>

          <div className="flex items-stretch gap-2">
            <input readOnly value={shareUrl} data-testid="share-link-input"
              onFocus={(e) => e.target.select()}
              className="flex-1 bg-zinc-900 border border-zinc-800 text-zinc-400 text-xs px-3 py-2 outline-none truncate" />
            <button data-testid="share-copy-link-btn" onClick={copy}
              className="shrink-0 inline-flex items-center gap-1.5 px-3 font-cinzel uppercase tracking-wide text-xs rounded-sm bg-amber-600 text-zinc-950 hover:bg-amber-500 transition-colors">
              {copied ? <Check size={14} /> : <Link2 size={14} />} {copied ? "Copied" : "Copy"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
